import React, { useCallback } from 'react';
import { Autocomplete, TextField, autocompleteClasses } from '@mui/material';

const ALL_OPTION = 'All';

interface CountryFilterSelectProps {
  /** Currently selected country value ('' means no filter / All). */
  value: string;
  /** Called with the selected country value, or '' when "All" is chosen / cleared. */
  onChange: (value: string) => void;
  /** Distinct country values pulled from the User table (raw, passed through verbatim). */
  options: string[];
  placeholder?: string;
}

/**
 * Searchable country dropdown for the admin User Search page. Unlike the shared
 * CountrySelect (which maps against the static COUNTRIES code/name constant), this
 * works over raw string values sourced from the distinct shipping-country values in
 * the User table, so it offers exactly the values that will produce hits and passes
 * the selected value through verbatim.
 */
const CountryFilterSelect: React.FC<CountryFilterSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Country',
}) => {
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string | null) => {
      onChange(!newValue || newValue === ALL_OPTION ? '' : newValue);
    },
    [onChange]
  );

  return (
    <Autocomplete
      value={value || ALL_OPTION}
      onChange={handleChange}
      options={[ALL_OPTION, ...options]}
      isOptionEqualToValue={(option, val) => option === val}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '42px',
              borderRadius: '8px',
              backgroundColor: '#1a1a1a',
              color: 'white',
              fontFamily: 'inherit',
              fontSize: '14px',
              border: '1px solid #2a2a2a',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
              '&.Mui-focused': { borderColor: '#7c3aed' },
            },
            '& .MuiOutlinedInput-input': {
              padding: '0 14px !important',
              color: 'white',
              fontFamily: 'inherit',
              fontSize: '14px',
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.4)',
                opacity: 1,
              },
            },
          }}
        />
      )}
      sx={{
        width: '100%',
        '& .MuiSvgIcon-root': { color: 'white' },
      }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#1a1a1a',
            color: 'white',
            fontFamily: 'inherit',
            fontSize: '14px',
            borderRadius: '8px',
            border: '1px solid #2a2a2a',
            marginTop: '4px',
            [`& .${autocompleteClasses.listbox}`]: {
              padding: 0,
              maxHeight: '300px',
              '& li': {
                padding: '8px 14px',
                fontFamily: 'inherit',
                fontSize: '14px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#2a2a2a' },
                '&[aria-selected="true"]': { backgroundColor: '#323232' },
              },
            },
          },
        },
      }}
    />
  );
};

export default CountryFilterSelect;
